import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import useWebSocket, { ReadyState } from "react-use-websocket";

export type SocketContextType = {
  isConnected: boolean;
  enviarMensagem: (message: string) => void;
  lastMessage: MessageEvent | null;
};

export const SocketContext = createContext<SocketContextType | undefined>(
  undefined
);

export function SocketProvider({ children }: { children: ReactNode }) {
  const { impressaoConfig } = useImpressaoConfig();
  const [socketUrl, setSocketUrl] = useState(import.meta.env.VITE_WS_URL);
  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    heartbeat: {
      message: "ping",
      interval: 5 * 60 * 1000,
    },
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });

  const enviarMensagem = (message: string) => {
    if (readyState === ReadyState.OPEN) {
      if (
        impressaoConfig?.hardwareid_alternativo &&
        impressaoConfig?.hardwareid_alternativo.length > 0
      ) {
        message =
          "HWID2|" + impressaoConfig.hardwareid_alternativo + "|" + message;
      }
      sendMessage(message);
    }
  };

  useEffect(() => {
    if (impressaoConfig?.app_id) {
      setSocketUrl(`${import.meta.env.VITE_WS_URL}/${impressaoConfig.app_id}`);
    }
  }, [impressaoConfig]);

  const isConnected = useMemo(
    () => readyState === ReadyState.OPEN,
    [readyState]
  );

  return (
    <SocketContext.Provider
      value={{
        isConnected,
        lastMessage,
        enviarMensagem,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket deve ser usado dentro de um SocketProvider");
  }
  return context;
}
